import type { LucideIcon } from 'lucide-react';
import { Tag } from 'lucide-react';
import type { PromotionPolicyToken } from '@sleed/bizcommerce-api';
import type { PolicyGradient } from '~/components/promotion/policy-badge-thumb';
import type { ActionType } from '~/lib/schemas/actions';
import { ACTION_TYPE_META_MAP, WIRE_TO_ACTION_TYPE } from '~/components/promotion/actions/action-type-meta';
import type { PromotionGridRow } from '~/lib/fixtures/demo-grid';

const GRADIENTS: PolicyGradient[] = ['sun', 'gold', 'dark', 'rose', 'cool', 'purple'];

function isoToDate(s: string | null): Date | null {
	if (!s) return null;
	const d = new Date(s);
	return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Maps a real PromotionPolicyToken to the grid view-model. thumbGradient/thumbIcon
 * are UI-only (not on the token): gradient is deterministic by id, icon is the first
 * action's icon (or Tag). Drafts (no code / no actions) get the dashed `draft` thumb.
 */
export function tokenToGridRow(token: PromotionPolicyToken): PromotionGridRow {
	const id = token.id ?? 0;
	const actionTypes = (token.CartActions ?? []).map(a => WIRE_TO_ACTION_TYPE[a.ActionType ?? '']).filter(Boolean) as ActionType[];
	const isDraft = !token.Code || actionTypes.length === 0;
	const firstIcon: LucideIcon = actionTypes[0] ? ACTION_TYPE_META_MAP[actionTypes[0]].icon : Tag;

	return {
		id,
		name: token.Name ?? '',
		code: token.Code ?? null,
		priority: token.Priority ?? token.si ?? 0,
		thumbGradient: isDraft ? 'draft' : GRADIENTS[id % GRADIENTS.length],
		thumbIcon: firstIcon,
		actionTypes,
		isActive: token.IsActive ?? true,
		activeFrom: isoToDate(token.ActiveFrom as string | null),
		activeTo: isoToDate(token.ActiveTo as string | null),
		isVisible: token.iv ?? true,
		hideFromOffers: token.HideFromOffers ?? false
	};
}
